import { useEffect, useMemo, useState } from "react";
import "./../styles/rank.css";
import AccountLayout from "../layouts/AccountLayout";
import { HashLoader } from "react-spinners";
import { showToast } from "../components/toasts/ToastNotification";
import { getUserTotalDeposit } from "../api/user.api";
import { useUser } from "../context/UserContext";

const rankData = [
  { level: 6, title: "VIP", points: 100000000, color: "#134977" }, // Original
  { level: 5, title: "Kim cương", points: 40000000, color: "#1A5A94" }, // Slightly lighter
  { level: 4, title: "Bạch kim", points: 20000000, color: "#2C6CA3" },
  { level: 3, title: "Vàng", points: 2000000, color: "#4781B2" },
  { level: 2, title: "Bạc", points: 1000000, color: "#6A9BC5" },
  { level: 1, title: "Đồng", points: 0, color: "#8CB5D8" }, // Lightest
];

const Rank = () => {
  const [userPoints, setUserPoints] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user } = useUser();

  // Function to determine if a rank is achieved
  const isRankAchieved = (rankPoints) => userPoints >= rankPoints;

  useEffect(() => {
    if (!user?._id) return;
    fetchUserTotalDeposit();
    document.title = "Xếp hạng của bạn";
  }, [user?._id]);

  const fetchUserTotalDeposit = async () => {
    setLoading(true);

    try {
      const res = await getUserTotalDeposit(user?._id);

      if (res.success) {
        setUserPoints(res.data?.totalDeposit || 0);
      }
    } catch (error) {
      showToast(error.message, "error");
    }
    finally {
      setLoading(false);
    }
  };

  // Function to determine current rank
  const getCurrentRank = () => {
    for (let i = 0; i < rankData.length; i++) {
      if (userPoints >= rankData[i].points) {
        return rankData[i];
      }
    }
    return rankData[rankData.length - 1];
  };

  // Determine next rank
  const getNextRank = () => {
    const currentRankIndex = rankData.findIndex(
      (rank) => rank.points <= userPoints
    );
    if (currentRankIndex <= 0) return null; // Already at highest rank
    return rankData[currentRankIndex - 1];
  };

  const currentRank = getCurrentRank();
  const nextRank = getNextRank();
  const remainingAmount = nextRank ? Math.max(nextRank.points - userPoints, 0) : 0;
  const rankRoadmap = useMemo(
    () => [...rankData].sort((a, b) => a.points - b.points),
    []
  );

  // Calculate progress percentage to next rank
  const calculateProgress = () => {
    if (!nextRank) return 100;

    const rangeStart = currentRank.points;
    const rangeEnd = nextRank.points;
    const pointsInRange = rangeEnd - rangeStart;
    const userPointsInRange = userPoints - rangeStart;

    return Math.min(Math.floor((userPointsInRange / pointsInRange) * 100), 100);
  };

  const progressPercentage = calculateProgress();
  const currentRoadmapIndex = rankRoadmap.findIndex((rank) => rank.level === currentRank.level);
  const roadmapSegments = Math.max(rankRoadmap.length - 1, 1);
  const timelineProgressRaw = nextRank
    ? ((currentRoadmapIndex + (progressPercentage / 100)) / roadmapSegments) * 100
    : 100;
  const timelineProgress = Math.min(Math.max(timelineProgressRaw, 0), 100);
  const timelineProgressWidth = userPoints > 0 ? Math.max(timelineProgress, 2) : 0;
  const overallProgressText = userPoints > 0 ? `${timelineProgress.toFixed(1)}%` : "0%";
  const achievedRanks = useMemo(
    () => rankData.filter((rank) => isRankAchieved(rank.points)).length,
    [userPoints]
  );

  return (
    <AccountLayout title="Xếp hạng của bạn">
      <div className="rank-page">
        {loading ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" size={30} />
          </div>
        ) : (
          <>
            <section className="rank-hero">
              <div className="rank-hero-copy">
                <p className="rank-eyebrow">Player Progression</p>
                <h2 className="rank-title">Xếp hạng của bạn</h2>
                <p className="rank-subtitle">Nạp càng nhiều, mở khóa càng cao và nhận đặc quyền tốt hơn.</p>
                <div className="rank-hero-chips">
                  <span className="rank-chip">Hiện tại: {currentRank.title}</span>
                  <span className="rank-chip">Mốc đã mở: {achievedRanks}/{rankData.length}</span>
                </div>
              </div>

              <div className="rank-hero-stats">
                <div className="rank-stat-card">
                  <p>Tổng nạp tích lũy</p>
                  <h3>{userPoints.toLocaleString()}đ</h3>
                </div>
                <div className="rank-stat-card">
                  <p>Tiến độ lên hạng</p>
                  <h3>{progressPercentage}%</h3>
                  {nextRank ? (
                    <span>Còn {remainingAmount.toLocaleString()}đ để lên {nextRank.title}</span>
                  ) : (
                    <span>Đã đạt cấp cao nhất</span>
                  )}
                </div>
              </div>
            </section>

            <section className="rank-progress-panel">
              <div className="rank-progress-head">
                <p>Tiến trình toàn hệ thống</p>
                <strong>{overallProgressText}</strong>
              </div>
              <div className="rank-progress-track">
                <div
                  className="rank-progress-fill"
                  style={{
                    width: `${timelineProgressWidth}%`,
                    background: `linear-gradient(90deg, ${rankRoadmap[0].color}, ${currentRank.color})`,
                  }}
                />
                {rankRoadmap.map((rank, index) => {
                  const achieved = isRankAchieved(rank.points);
                  const isCurrent = currentRank.level === rank.level;
                  const leftPercent = roadmapSegments === 0 ? 0 : (index / roadmapSegments) * 100;
                  const tooltipText = `${rank.title} • ${rank.points.toLocaleString()}đ • ${
                    isCurrent ? "Đang ở cấp này" : achieved ? "Đã mở khóa" : "Chưa đạt"
                  }`;
                  return (
                    <div
                      key={rank.level}
                      className={`rank-node ${achieved ? "achieved" : ""} ${isCurrent ? "current" : ""} ${index === 0 ? "start" : ""} ${index === rankRoadmap.length - 1 ? "end" : ""}`}
                      style={{ left: `${leftPercent}%` }}
                      data-tooltip={tooltipText}
                    >
                      <div className="rank-node-dot" />
                      <p className="rank-node-label">{rank.title}</p>
                      <p className="rank-node-points">{rank.points.toLocaleString()}đ</p>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </AccountLayout>
  );
};

export default Rank;
